import { RiskWithVulnerability } from '@/sections/insights/queryBuilder/legacy/types/queryBuilder.types';
import { Asset, Seed, Technology } from '@/types';
import { formatDate } from '@/utils/date.util';
import { parseTechnologyVersion } from '@/utils/entities/technology.util';
import { getVulnerabilityStatusLabel } from '@/utils/entities/vulnerability.util';
import { EntityType, getEntityFieldLabel } from '@/utils/entityFilters.util';

export interface BaseEntity {
  key?: string;
  name?: string;
  dns?: string;
  status?: string;
  source?: string;
  integrationSource?: string;
  visited?: string;
  created?: string;
  priority?: number;
  cvss?: number;
  epss?: number;
  kev?: boolean;
  exploit?: boolean;
  attributes?: Array<{ name: string; value: unknown }>;
  [key: string]: unknown;
}

export interface ADObject extends BaseEntity {
  class:
    | 'user'
    | 'group'
    | 'computer'
    | 'domain'
    | 'container'
    | 'certtemplate'
    | 'aiaca'
    | 'ou'
    | 'gpo'
    | 'issuancepolicy'
    | 'rootca'
    | 'enterpriseca'
    | 'ntauthstore'
    | 'aduser'
    | 'adgroup'
    | 'adcomputer'
    | 'addomain'
    | 'adcontainer'
    | 'adcerttemplate'
    | 'adaiaca';
  label:
    | 'AD User'
    | 'AD Group'
    | 'AD Computer'
    | 'AD Domain'
    | 'AD Container'
    | 'AD CertTemplate'
    | 'AD AIACA';
  domain?: string;
  domainsid?: string;
  sid?: string;
  objectid?: string;
  distinguishedname?: string;
  samaccountname?: string;
  admincount?: boolean;
  isaclprotected?: boolean;
  useraccountcontrol?: number;
  pwdneverexpires?: boolean;
  lastlogon?: number;
  lastlogontimestamp?: number;
  groupscope?: string;
  isdc?: boolean;
  unconstraineddelegation?: boolean;
  supportedencryptiontypes?: string;
  ldapavailable?: boolean;
  ldapsavailable?: boolean;
  displayname?: string;
  authenticationenabled?: boolean;
  enrollmentflag?: number;
  validityperiod?: string;
  renewalperiod?: string;
}

export type QueryResultEntity =
  | RiskWithVulnerability
  | Asset
  | Technology
  | Seed
  | ADObject
  | BaseEntity;

export const isAsset = (entity: QueryResultEntity): entity is Asset => {
  if (!entity || typeof entity !== 'object') return false;

  if (
    'key' in entity &&
    typeof entity.key === 'string' &&
    entity.key.startsWith('#asset#')
  ) {
    return true;
  }

  if (
    'key' in entity &&
    typeof entity.key === 'string' &&
    (entity.key.startsWith('#aduser#') ||
      entity.key.startsWith('#adgroup#') ||
      entity.key.startsWith('#adcomputer#') ||
      entity.key.startsWith('#addomain#') ||
      entity.key.startsWith('#adcontainer#') ||
      entity.key.startsWith('#adcerttemplate#') ||
      entity.key.startsWith('#adaiaca#'))
  ) {
    return true;
  }

  if ('status' in entity && typeof entity.status === 'string') {
    return ['A', 'P', 'U', 'I', 'F', 'D', 'FR', 'AP', 'AL'].includes(
      entity.status
    );
  }

  return false;
};

export const isTechnology = (
  entity: QueryResultEntity
): entity is Technology => {
  if (!entity || typeof entity !== 'object') return false;

  if (
    'key' in entity &&
    typeof entity.key === 'string' &&
    entity.key.startsWith('#technology#')
  ) {
    return true;
  }

  if ('vendor' in entity && 'product' in entity) {
    return true;
  }

  return false;
};

export const isVulnerability = (
  entity: QueryResultEntity
): entity is RiskWithVulnerability => {
  if (!entity || typeof entity !== 'object') return false;

  if ('priority' in entity && typeof entity.priority === 'number') {
    return true;
  }

  if (
    'key' in entity &&
    typeof entity.key === 'string' &&
    entity.key.startsWith('#risk#')
  ) {
    return true;
  }

  if (
    'status' in entity &&
    typeof entity.status === 'string' &&
    entity.status.length === 2
  ) {
    return ['T', 'O', 'I', 'R'].includes(entity.status[0]);
  }

  return false;
};

export const isSeed = (entity: QueryResultEntity): entity is Seed => {
  if (!entity || typeof entity !== 'object') return false;

  if (
    'key' in entity &&
    typeof entity.key === 'string' &&
    entity.key.startsWith('#seed#')
  ) {
    return true;
  }

  if ('type' in entity && 'domain' in entity) {
    return true;
  }

  return false;
};

export const isADObject = (entity: QueryResultEntity): entity is ADObject => {
  if (!entity || typeof entity !== 'object') return false;

  if (
    'key' in entity &&
    typeof entity.key === 'string' &&
    (entity.key.startsWith('#aduser#') ||
      entity.key.startsWith('#adgroup#') ||
      entity.key.startsWith('#adcomputer#') ||
      entity.key.startsWith('#addomain#') ||
      entity.key.startsWith('#adcontainer#') ||
      entity.key.startsWith('#adcerttemplate#') ||
      entity.key.startsWith('#adaiaca#') ||
      entity.key.startsWith('#adou#') ||
      entity.key.startsWith('#adgpo#') ||
      entity.key.startsWith('#adlocalgroup#') ||
      entity.key.startsWith('#adlocaluser#') ||
      entity.key.startsWith('#adrootca#') ||
      entity.key.startsWith('#adenterpriseca#') ||
      entity.key.startsWith('#adntauthstore#') ||
      entity.key.startsWith('#adissuancepolicy#'))
  ) {
    return true;
  }

  if (
    'class' in entity &&
    typeof entity.class === 'string' &&
    (entity.class === 'aduser' ||
      entity.class === 'adgroup' ||
      entity.class === 'adcomputer' ||
      entity.class === 'addomain' ||
      entity.class === 'adcontainer' ||
      entity.class === 'adcerttemplate' ||
      entity.class === 'adaiaca' ||
      entity.class === 'adou' ||
      entity.class === 'adgpo' ||
      entity.class === 'adlocalgroup' ||
      entity.class === 'adlocaluser' ||
      entity.class === 'adrootca' ||
      entity.class === 'adenterpriseca' ||
      entity.class === 'adntauthstore' ||
      entity.class === 'adissuancepolicy')
  ) {
    return true;
  }

  if (
    'label' in entity &&
    typeof entity.label === 'string' &&
    (entity.label === 'AD User' ||
      entity.label === 'AD Group' ||
      entity.label === 'AD Computer' ||
      entity.label === 'AD Domain' ||
      entity.label === 'AD Container' ||
      entity.label === 'AD CertTemplate' ||
      entity.label === 'AD AIACA' ||
      entity.label === 'ADUser' ||
      entity.label === 'ADGroup' ||
      entity.label === 'ADComputer' ||
      entity.label === 'ADDomain' ||
      entity.label === 'ADContainer' ||
      entity.label === 'ADCertTemplate' ||
      entity.label === 'ADAIACA')
  ) {
    return true;
  }

  return false;
};

export const detectEntityType = (
  entity: QueryResultEntity
):
  | 'asset'
  | 'vulnerability'
  | 'technology'
  | 'seed'
  | 'adobject'
  | 'unknown' => {
  if (isADObject(entity)) return 'adobject';
  if (isAsset(entity)) return 'asset';
  if (isVulnerability(entity)) return 'vulnerability';
  if (isTechnology(entity)) return 'technology';
  if (isSeed(entity)) return 'seed';
  return 'unknown';
};

const ENTITY_FIELD_MAPPINGS = {
  technology: {
    vendor: 'technologyVendor',
    product: 'technologyProduct',
    version: 'technologyVersion',
    name: 'technologyName',
    key: 'technologyKey',
  },
  asset: {
    status: 'assetStatus',
    dns: 'assetDns',
    name: 'assetName',
    key: 'assetKey',
    visited: 'assetVisited',
    created: 'assetCreated',
    class: 'assetClass',
    asname: 'assetAsname',
    asnumber: 'assetAsnumber',
  },
  vulnerability: {
    status: 'vulnerabilityStatus',
    priority: 'vulnerabilityPriority',
    name: 'vulnerabilityName',
    key: 'vulnerabilityKey',
    cvss: 'vulnerabilityCvss',
    epss: 'vulnerabilityEpss',
    kev: 'vulnerabilityKev',
    exploit: 'vulnerabilityExploit',
    created: 'vulnerabilityCreated',
    updated: 'vulnerabilityUpdated',
  },
  seed: {
    status: 'seedStatus',
    dns: 'seedDns',
    registrant: 'seedRegistrant',
    registrar: 'seedRegistrar',
    email: 'seedEmail',
    country: 'seedCountry',
    name: 'seedName',
    key: 'seedKey',
  },
} as const;

export const getEntityProperty = (
  entity: QueryResultEntity,
  property: keyof BaseEntity | string
): string => {
  if (property in entity) {
    const value = (entity as BaseEntity)[property];
    return value !== undefined && value !== null ? String(value) : '';
  }

  const entityData = entity as Record<string, unknown>;

  if (property === 'adObjectType') {
    return getADObjectTypeLabel(entity);
  }

  if (property === 'tags') {
    const tags = entityData.tags;
    if (Array.isArray(tags)) {
      return tags.join(', ');
    }
    return '';
  }

  for (const fieldMappings of Object.values(ENTITY_FIELD_MAPPINGS)) {
    if (property in fieldMappings) {
      const enrichedFieldName =
        fieldMappings[property as keyof typeof fieldMappings];
      if (
        entityData[enrichedFieldName] !== undefined &&
        entityData[enrichedFieldName] !== null
      ) {
        return String(entityData[enrichedFieldName]);
      }
    }
  }

  const baseEntity = entity as BaseEntity;
  if (baseEntity.attributes && Array.isArray(baseEntity.attributes)) {
    const attributeFields = ['protocol', 'port', 'cloud', 'tags', 'new'];
    if (attributeFields.includes(property as string)) {
      const matchingAttribute = baseEntity.attributes.find(
        attr => attr.name === property
      );
      if (matchingAttribute) {
        return String(matchingAttribute.value);
      }
    }
  }

  const attributeFields = ['protocol', 'port', 'cloud', 'tags', 'new'];
  if (attributeFields.includes(property as string)) {
    const entityWithAttributes = entity as Record<string, unknown>;
    const directValue = entityWithAttributes[property as string];
    if (directValue !== undefined && directValue !== null) {
      return String(directValue);
    }
  }

  return '';
};

export const formatFieldValue = (
  entity: QueryResultEntity,
  field: string,
  column: { entityType: string }
): string | unknown[] => {
  const entityData = entity as Record<string, unknown>;
  const aggregatedFieldName = `all${field.charAt(0).toUpperCase() + field.slice(1)}`;
  const aggregatedValue = entityData[aggregatedFieldName];

  if (Array.isArray(aggregatedValue) && aggregatedValue.length > 0) {
    if (['cvss', 'epss'].includes(field)) {
      const uniqueValues = [
        ...new Set(
          aggregatedValue
            .map(v => {
              const numValue = parseFloat(String(v));
              if (!isNaN(numValue)) {
                return field === 'epss'
                  ? numValue.toFixed(3)
                  : numValue.toFixed(1);
              }
              return null;
            })
            .filter(v => v !== null)
        ),
      ];

      if (uniqueValues.length === 1) {
        return uniqueValues[0];
      }

      return uniqueValues;
    }

    return aggregatedValue;
  }

  const value = getEntityProperty(entity, field as keyof BaseEntity);
  if (!value) return '-';

  if (field === 'tags' && typeof value === 'string') {
    const tagArray = value.split(', ').filter(tag => tag.trim() !== '');
    return tagArray.length > 0 ? tagArray : '-';
  }

  if (field === 'adObjectType') {
    const entityData = entity as Record<string, unknown>;

    if (isADObject(entity)) {
      return getADObjectTypeLabel(entity);
    }

    if (entityData.class && typeof entityData.class === 'string') {
      const adClassValues = [
        'user',
        'group',
        'computer',
        'domain',
        'container',
        'certtemplate',
        'aiaca',
        'ou',
        'gpo',
        'issuancepolicy',
        'rootca',
        'enterpriseca',
        'ntauthstore',
        'aduser',
        'adgroup',
        'adcomputer',
        'addomain',
        'adcontainer',
        'adcerttemplate',
        'adaiaca',
      ];

      if (adClassValues.includes(entityData.class.toLowerCase())) {
        return getADObjectTypeLabel(entity);
      }
    }

    if (entityData.key && typeof entityData.key === 'string') {
      const adKeyPrefixes = [
        '#aduser#',
        '#adgroup#',
        '#adcomputer#',
        '#addomain#',
        '#adcontainer#',
        '#adcerttemplate#',
        '#adaiaca#',
      ];

      for (const prefix of adKeyPrefixes) {
        if (entityData.key.startsWith(prefix)) {
          return getADObjectTypeLabel(entity);
        }
      }
    }

    return '-';
  }

  if (field === 'status') {
    if (column.entityType === 'Vulnerability' && isVulnerability(entity)) {
      return getVulnerabilityStatusLabel(entity.status);
    }

    return value;
  }

  if (['created', 'visited'].includes(field)) {
    return value ? formatDate(value) : '-';
  }

  if (['kev', 'exploit'].includes(field)) {
    return value === 'true' ? 'Yes' : value === 'false' ? 'No' : '-';
  }

  if (field === 'version' && isTechnology(entity)) {
    return parseTechnologyVersion(entity) || '-';
  }

  if (['cvss', 'epss'].includes(field)) {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      return field === 'epss' ? numValue.toFixed(3) : numValue.toFixed(1);
    }
    return '-';
  }

  return value;
};

export const generateFieldLabel = (
  fieldName: string,
  entityType: string
): string => {
  const queryResultSpecificLabels: Record<string, string> = {
    cvss: 'CVSS Score',
    epss: 'EPSS Score',
    kev: 'CISA KEV',
    exploit: 'Public Exploits',
    dns: 'Fully Qualified Domain Name',
    adObjectType: 'Object Type',
    isdc: 'Domain Controller',
    lsdc: 'Domain Controller',
    isaclprotected: 'ACL Protected',
  };

  const entitySpecificOverrides: Record<string, Record<string, string>> = {
    Vulnerability: {
      priority: 'Severity',
      status: 'Vulnerability Status',
      created: 'First Seen',
      visited: 'Last Seen',
    },
    Asset: {
      name: 'IP Address',
      status: 'Asset Status',
      created: 'First Seen',
      visited: 'Last Seen',
    },
    Seed: {
      registrant: 'Registrant',
      type: 'Type',
      domain: 'Domain',
      email: 'Email',
      registrar: 'Registrar',
    },
  };

  if (entitySpecificOverrides[entityType]?.[fieldName]) {
    return entitySpecificOverrides[entityType][fieldName];
  }

  if (queryResultSpecificLabels[fieldName]) {
    return queryResultSpecificLabels[fieldName];
  }

  const entityTypeMap: Record<string, EntityType> = {
    Asset: 'Asset',
    Vulnerability: 'Vulnerability',
    Technology: 'Technology',
    Seed: 'Seed',
    ActiveDirectory: 'ActiveDirectory',
  };

  const mappedEntityType = entityTypeMap[entityType];
  if (mappedEntityType) {
    return getEntityFieldLabel(mappedEntityType, fieldName);
  }

  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

export const getADObjectTypeLabel = (
  adObject: ADObject | QueryResultEntity
): string => {
  const entityClass = (adObject as Record<string, unknown>).class as
    | string
    | undefined;

  if (!entityClass && 'key' in adObject && typeof adObject.key === 'string') {
    if (adObject.key.startsWith('#aduser#')) return 'AD User';
    if (adObject.key.startsWith('#adgroup#')) return 'AD Group';
    if (adObject.key.startsWith('#adcomputer#')) return 'AD Computer';
    if (adObject.key.startsWith('#addomain#')) return 'AD Domain';
    if (adObject.key.startsWith('#adcontainer#')) return 'AD Container';
    if (adObject.key.startsWith('#adcerttemplate#'))
      return 'Certificate Template';
    if (adObject.key.startsWith('#adaiaca#')) return 'AIA CA';
  }

  if (typeof entityClass === 'string') {
    switch (entityClass.toLowerCase()) {
      case 'user':
      case 'aduser':
        return 'AD User';
      case 'group':
      case 'adgroup':
        return 'AD Group';
      case 'computer':
      case 'adcomputer':
        return 'AD Computer';
      case 'domain':
      case 'addomain':
        return 'AD Domain';
      case 'container':
      case 'adcontainer':
        return 'AD Container';
      case 'certtemplate':
      case 'adcerttemplate':
        return 'Certificate Template';
      case 'aiaca':
      case 'adaiaca':
        return 'AIA CA';
      case 'ou':
        return 'Organizational Unit';
      case 'gpo':
        return 'Group Policy Object';
      case 'issuancepolicy':
        return 'Issuance Policy';
      case 'rootca':
        return 'Root CA';
      case 'enterpriseca':
        return 'Enterprise CA';
      case 'ntauthstore':
        return 'NT Auth Store';
      default:
        return `AD ${entityClass.charAt(0).toUpperCase() + entityClass.slice(1)}`;
    }
  }

  return 'AD Object';
};

export const getADObjectDisplayName = (adObject: ADObject): string => {
  if (adObject.class === 'aduser') {
    return String(
      adObject.samaccountname ||
        adObject.name ||
        adObject.identifier ||
        'Unknown User'
    );
  }

  if (adObject.class === 'adcomputer') {
    const name = adObject.name || adObject.identifier;
    if (typeof name === 'string' && name.length > 0) {
      return name.split('.')[0] || 'Unknown Computer';
    }
    return 'Unknown Computer';
  }

  if (adObject.class === 'addomain' || adObject.class === 'domain') {
    return String(
      adObject.domain ||
        adObject.name ||
        adObject.identifier ||
        'Unknown Domain'
    );
  }

  return String(adObject.name || adObject.identifier || 'Unknown');
};
